type StackDirection = 'row' | 'column';

type ResponsiveStackDirection = {
  xs?: StackDirection;
  sm?: StackDirection;
  md?: StackDirection;
  lg?: StackDirection;
  xl?: StackDirection;
  xxl?: StackDirection;
};

type StackAlignItems = 'flex-start' | 'center' | 'flex-end';

type ResponsiveStackAlignItems = {
  xs?: StackAlignItems;
  sm?: StackAlignItems;
  md?: StackAlignItems;
  lg?: StackAlignItems;
  xl?: StackAlignItems;
  xxl?: StackAlignItems;
};

const DEFAULT_STACK_DIRECTION = { xs: 'column', sm: 'row' } as const;
const DEFAULT_STACK_ALIGNMENT = { xs: 'center' } as const;

const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

type Breakpoint = (typeof BREAKPOINTS)[number];

const BREAKPOINT_SELECTION_PATTERN = /^(xs|sm|md|lg|xl|xxl) - ([a-z]+)$/;

const parseResponsiveSelections = <TValue>(
  selections: string[],
  isValidValue: (value: string) => boolean,
  mapValue: (value: string) => TValue,
): Record<Breakpoint, TValue> | TValue | undefined => {
  const parsed: Partial<Record<Breakpoint, TValue>> = {};

  selections.forEach((selection) => {
    const match = selection.match(BREAKPOINT_SELECTION_PATTERN);
    if (!match) {
      return;
    }

    const [, breakpoint, rawValue] = match;

    if (!isValidValue(rawValue)) {
      return;
    }

    parsed[breakpoint as Breakpoint] = mapValue(rawValue);
  });

  const breakpoints = Object.keys(parsed) as Breakpoint[];

  if (breakpoints.length === 0) {
    return undefined;
  }

  if (breakpoints.length === 1) {
    const [breakpoint] = breakpoints;
    return parsed[breakpoint] as TValue;
  }

  return parsed as Record<Breakpoint, TValue>;
};

const isStackDirection = (value: string): value is StackDirection => {
  return value === 'column' || value === 'row';
};

const isStackAlignment = (
  value: string,
): value is keyof typeof STACK_ALIGNMENT_MAP => {
  return value in STACK_ALIGNMENT_MAP;
};

const STACK_ALIGNMENT_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
} as const;

const toResponsiveDirection = (
  selections: string[],
): ResponsiveStackDirection | StackDirection => {
  const direction = parseResponsiveSelections(
    selections,
    isStackDirection,
    (value) => {
      return value as StackDirection;
    },
  );

  if (direction === undefined) {
    return DEFAULT_STACK_DIRECTION;
  }

  return direction;
};

const toResponsiveAlignItems = (
  selections: string[],
): ResponsiveStackAlignItems | StackAlignItems | undefined => {
  return parseResponsiveSelections(selections, isStackAlignment, (value) => {
    return STACK_ALIGNMENT_MAP[value as keyof typeof STACK_ALIGNMENT_MAP];
  });
};

export const getButtonGroupStackDirection = (
  direction?: string | string[],
): ResponsiveStackDirection | StackDirection => {
  if (Array.isArray(direction)) {
    return toResponsiveDirection(direction);
  }

  if (direction === 'Row') {
    return 'row';
  }

  if (direction === 'Column') {
    return 'column';
  }

  return DEFAULT_STACK_DIRECTION;
};

export const getButtonGroupStackAlignItems = (
  alignment?: string | string[],
): ResponsiveStackAlignItems | StackAlignItems => {
  if (!Array.isArray(alignment) || alignment.length === 0) {
    return DEFAULT_STACK_ALIGNMENT;
  }

  const parsed = toResponsiveAlignItems(alignment);

  return parsed ?? DEFAULT_STACK_ALIGNMENT;
};
